import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class CuttingPlanService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves cutting plan
     *
     * @datatask 2880
     * @datataskName sp_ws_p2_w_cuttingplan_get
     * @param params the keyno of the cutting plan to get.
     * @returns promise containing the cutting plan.
     */
    public async getCuttingPlan(params: GetCuttingPlanParams): Promise<CuttingPlanGet> {
        return (await this.datatask.Post(2880, params))[0];
    }

    /**
     * Deletes the cutting plan with the given keyno.
     *
     * @datatask 4004
     * @datataskName sp_ws_p2_w_cuttingplan_delete
     * @param params the keyno of the cutting plan to delete.
     * @returns promise containing validation.
     */
    public async deleteCuttinglan(params: GetCuttingPlanParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4004, params))[0];
    }

    /**
     * Saves the given cutting plan.
     *
     * @datatask 2883
     * @datataskName sp_ws_p2_w_cuttingplan_save
     * @param params the cutting plan to save.
     * @returns promise containing validation for saving.
     */
    public async saveCuttingPlan(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2883, params))[0];
    }

    /**
     * Performs barcode scan for the given cutting plan get.
     *
     * @datatask 2885
     * @datataskName sp_ws_p2_w_cuttingplan_scan
     * @param params the cutting plan the scan occurs for.
     * @returns promise containing validation for scan.
     */
    public async scanCuttingPlan(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2885, params))[0];
    }

    /**
     * Prints the given cutting plan using the printer with the given printer keyno,
     *
     * @datatask 2934
     * @datataskName tt_cuttingplan_print
     * @param params the cuttingplan keyno and printer keyno.
     * @returns promise containing validation message for the print.
     */
    public async printCuttingPlan(params: PrintCuttingPlanParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2934, params))[0];
    }

    /**
     * Distributes the given cutting plan.
     *
     * @datatask 2889
     * @datataskName sp_ws_p2_w_cuttingplan_distribute_stock_qty
     * @param params the cuttingplan.
     * @returns promise containing validation message for the distribute.
     */
    public async distributeCuttingPlan(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2889, params))[0];
    }

    /**
     * Marks the cutting plan as finished.
     *
     * @datatask 4005
     * @datataskName sp_ws_p2_w_cuttingplan_finish
     * @param params the cuttingplan to finish.
     * @returns promise containing validation.
     */
    public async finishCuttingPlan(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4005, params))[0];
    }

    /**
     * Resets the pick for the given cutting plan.
     *
     * @datatask 4002
     * @datataskName sp_ws_p2_w_cuttingplan_reset_pick
     * @param params the cuttingplan to reset pick for.
     * @returns promise containing validation.
     */
    public async resetCuttingPlanPick(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4002, params))[0];
    }

    /**
     * Deletes the given po from the the connected cuttingplan.
     *
     * @datatask 4003
     * @datataskName sp_ws_p2_w_cuttingplan_cuttingplanpo_delete
     * @param params the cuttingplan po grid row to remove po cuttingplan connection for.
     * @returns promise containing validation.
     */
    public async deletePoFromCuttingPlan(params: CuttingPlanPoGridRow): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4003, params))[0];
    }

    /**
     * Retrieves a file for optimalized cutting plan.
     *
     * @datatask 3817
     * @datataskName sp_ws_p2_w_cuttingplan_cuttinglist_file
     * @param params the cutting plan.
     * @returns promise containing validation and the file content and file name.
     */
    public async getOptimalizedCuttingPlanFile(params: CuttingPlanGet): Promise<GetOptimalizedCuttingPlanFileResponse> {
        return (await this.datatask.Post(3817, params))[0];
    }

    /**
     * Sends changes and returns new changes.
     *
     * @datatask 3993
     * @datataskName sp_ws_p2_w_cuttingplan_colchanged
     * @param params the changed colname and the cutting plan.
     * @returns promise containing any new changes, if any.
     */
    public async changesCuttingPlan(params: { colname_changed: keyof CuttingPlanGet } & CuttingPlanGet): Promise<Partial<CuttingPlanGet>> {
        return (await this.datatask.Post(3993, params))[0];
    }

    /**
     * Changes the status of the cutting plan to the next status.
     *
     * @datatask 3995
     * @datataskName sp_ws_p2_w_cuttingplan_status_next
     * @param params cutting plan.
     * @returns promise containing validaiton.
     */
    public async updateToNextStatus(params: CuttingPlanGet): Promise<Partial<ErrorMessageResponse>> {
        return (await this.datatask.Post(3995, params))[0];
    }

    /**
     * Changes the status of the cutting plan to the previous status.
     *
     * @datatask 3994
     * @datataskName sp_ws_p2_w_cuttingplan_status_previous
     * @param params cutting plan.
     * @returns promise containing validaiton.
     */
    public async updateToPreviousStatus(params: CuttingPlanGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3994, params))[0];
    }

    /**
     * Retrieves list of products matching the given searchtext.
     *
     * @datatask 2696
     * @datataskName sp_ws_p2_w_cuttingplan_get_udef_list
     * @param params searchtext.
     * @returns promise containing list of products.
     */
    public getUdefProducts(params: GetUdefProductsParams): Promise<UdefProductItem[]> {
        return this.datatask.Post(2969, params);
    }

    /**
     * Retrieves list of formats for the cutting plan.
     *
     * @datatask 2882
     * @datataskName sp_ws_p2_w_cuttingplan_format_list
     * @param params settings for the list of formats.
     * @return promise containing list of formats.
     */
    public getFormats(params: GetFormatsParams): Promise<FormatItem[]> {
        return this.datatask.Post(2882, params);
    }

    /**
     * Retrieves list of cutting plan statuses.
     *
     * @datatask 2879
     * @datataskName sp_ws_p2_cuttingplanstatuses_list
     * @returns promise containing list of statuses.
     */
    public getStatuses(params?: GetCuttingPlanStatusParams): Promise<StatusItem[]> {
        return this.datatask.Post(2879, params);
    }

    /**
     * Retrieves the get for cutting plan real.
     *
     * @datatask 3997
     * @datataskName sp_ws_p2_w_cuttingplanreal_get
     * @param params the cuttingplan.
     * @returns promise containing cutting plan real get.
     */
    public async getCuttingPlanReal(params: GetCuttingPlanParams): Promise<CuttingPlanRealGet> {
        return (await this.datatask.Post(3997, params))[0];
    }

    /**
     * Sends changes and returns new changes.
     *
     * @datatask 3998
     * @datataskName sp_ws_p2_w_cuttingplanreal_colchanged
     * @param params the changed colname and the cutting plan.
     * @returns promise containing any new changes, if any.
     */
    public async changesCuttingPlanReal(params: ChangesCuttingPlanRealParams): Promise<Partial<CuttingPlanRealGet>> {
        return (await this.datatask.Post(3998, params))[0];
    }

    /**
     * Saves the given cutting plan real.
     *
     * @datatask 4000
     * @datataskName sp_ws_p2_w_cuttingplanreal_save
     * @param params
     * @returns promise containing validation.
     */
    public async saveCuttingPlanReal(params: CuttingPlanRealGet & { records: any[] }): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4000, params))[0];
    }

    /**
     * Distributes the given cutting plan real.
     *
     * @datatask 4001
     * @datataskName sp_ws_p2_w_cuttingplanreal_distribute
     * @param params
     * @returns promise containing validation.
     */
    public async distributeCuttingPlanReal(params: CuttingPlanRealGet & { records: any[] }): Promise<{ stockbatch_keyno: string | number; quantity_picked_now: string | number }[]> {
        return this.datatask.Post(4001, params);
    }

    /**
     * @datatask 4011
     * @datataskName sp_ws_p2_w_cuttingplans_switch_component
     * @param params the prod id and keyno to update to.
     * @returns promise containing validation.
     */
    public async updateCuttingPlanComponent(params: UpdateCuttingPlanComponentParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4011, params))[0];
    }
}

export interface UpdateCuttingPlanComponentParams {
    prod_id: string;
    comp_po_keyno: string | number;
}

export interface CuttingPlanRealGet extends CuttingPlanGet {
    webpage_heading: string;
}

export interface GetOptimalizedCuttingPlanFileResponse extends ErrorMessageResponse {
    filecontent: string;
    filename: string;
}

export interface PrintCuttingPlanParams {
    cuttingplan_keyno: string | number;
    printer_keyno: string | number;
}

export interface StatusItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface GetFormatsParams {
    prod_id: string;
}

export interface FormatItem {
    componentalternative_keyno: string;
    prod_name: string;
    prod_id: string;
    orderby: string;
}

export interface GetUdefProductsParams {
    searchtext: string;
}

export interface UdefProductItem {
    prod_id: string;
    prod_keyname: string;
    orderby: string;
}

export interface GetCuttingPlanParams {
    cuttingplan_keyno: string;
}

export interface GetCuttingPlanStatusParams {
    /**
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface CuttingPlanGet {
    prod_keyname?: string;
    printer_keyno?: string;
    cuttingplan_keyno: string | number;
    cuttingplan_name: string;
    businessco_no: string | number;
    reg_datetime: string;
    reg_by_portal_user_keyno: string | number;
    prod_id: string;
    prod_name: string;
    cuttingplanstatus_no: string;
    stock_quantity_pre_optimalization: string | number;
    stock_quantity_after_optimalization: string | number;
    quantity_to_pick: string | number;
    unit_to_pick: string | number;
    stock_quantity_to_pick: string | number;
    factory_id: string;
    workgrp_keyno: string | number;
    equipment_id: string;
    operation_id: string;
    componentalternative_keyno: string | number;
    workingtime_hours: string | number;
    stockplace_id: string;
    barcode: string;
    gb_cuttingplan_main: string;
    show_setting: '1' | '0';
    stock_unit_name: string;
    stock_keyno: string | number;
    pohcompletionprinciple_keyno: string;
    pohcompletionprinciple_name: string;
    stock_stockvalue_ex_assigned: string | number;
    show_pick: '1' | '0';
    show_reset_pick: '1' | '0';
    prod_id_undefined_format: string;
    prod_name_undefined_format: string;
}

export interface ChangesCuttingPlanParams extends CuttingPlanGet {
    colname_changed: keyof CuttingPlanGet;
}

export interface ChangesCuttingPlanRealParams extends CuttingPlanRealGet {
    colname_changed: keyof CuttingPlanRealGet;
}

export interface CuttingPlanPoGridRow {
    cuttingplanpo_keyno: number;
    cuttingplan_keyno: number;
    reg_datetime: Date;
    reg_by_portal_user_name: string;
    po_keyno: number;
    prod_id: string;
    prod_name: string;
    quantity: number;
    unit_name: string;
    is_selected: boolean;
    p2_pofinish_keyno: number;
}

export interface CuttingPlanCompGridRow {
    cuttingplanpo_keyno: number;
    cuttingplan_keyno: number;
    reg_datetime: Date;
    reg_by_portal_user_name: string;
    po_keyno: number;
    prod_id: string;
    prod_name: string;
    quantity: number;
    unit_name_prod: string;
    is_selected: boolean;
    po_keyno_comp: number;
    need_stock_quantity: number;
    stock_quantity_distributed: number;
    comp_prod_id: string;
    comp_prod_name: string;
    unit_name_comp: string;
}
